import React, { useState } from 'react';
import { THEMES } from '../../../themes/themes';
import { THEME_VARIANTS } from './constants';

// Forge-V2 widget set — ported verbatim from
// prototypes/campaign-creation-prototype.jsx. These widgets are tightly
// coupled to the forge's visual language (small inputs, atmospheric
// choice cards, segmented link-mode controls). Kept local to forge-v2/
// to avoid polluting the dashboard's general-purpose primitives.

// -----------------------------------------------------------------------
// StepPage — shared layout wrapper for each step. Two modes:
//   compact (false) — dramatic centered display-serif heading + eyebrow
//   compact (true)  — small left-aligned heading; no eyebrow / subtitle
// -----------------------------------------------------------------------
export const StepPage = ({ eyebrow, title, subtitle, children, theme, compact = false }) => {
  const c = theme.colors;
  const t = theme.typography || {};
  const e = theme.effects || {};

  if (compact) {
    return (
      <div style={{ padding: '24px 28px 20px' }}>
        {title && (
          <h2 style={{
            margin: '0 0 20px',
            fontFamily: t.headingFamily,
            fontSize: '1.7rem',
            fontWeight: 600,
            color: c.accentLight,
            letterSpacing: '0.01em',
            lineHeight: 1.2,
          }}>{title}</h2>
        )}
        {children}
      </div>
    );
  }

  return (
    <div style={{ padding: '28px 28px 24px' }}>
      {(eyebrow || title || subtitle) && (
        <div style={{ textAlign: 'center', marginBottom: 24 }}>
          {eyebrow && (
            <div style={{
              fontSize: '0.7rem',
              letterSpacing: '0.28em',
              color: c.accent,
              textTransform: 'uppercase',
              fontWeight: 700,
              fontFamily: t.fontFamily,
              marginBottom: 12,
            }}>{eyebrow}</div>
          )}
          {title && (
            <h1 style={{
              margin: 0,
              fontFamily: t.headingFamily,
              fontSize: '1.8rem',
              fontWeight: 600,
              color: c.accentLight,
              letterSpacing: '0.01em',
              textShadow: e.headingTextShadow,
              lineHeight: 1.2,
            }}>{title}</h1>
          )}
          {subtitle && (
            <div style={{
              color: c.textSecondary,
              fontSize: '1.05rem',
              fontStyle: 'italic',
              fontFamily: t.fontFamily,
              maxWidth: 520,
              margin: '12px auto 0',
              lineHeight: 1.5,
            }}>{subtitle}</div>
          )}
        </div>
      )}
      {children}
    </div>
  );
};

// -----------------------------------------------------------------------
// TextInput — `big` switch toggles between the dramatic centered display-
// serif input used for the name field and the regular compact field used
// for tagline / other text fields.
// -----------------------------------------------------------------------
export const TextInput = ({ value, onChange, placeholder, theme, big = false, autoFocus, maxLength }) => {
  const c = theme.colors;
  const t = theme.typography || {};
  const [focused, setFocused] = useState(false);
  return (
    <input
      type="text"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      onFocus={() => setFocused(true)}
      onBlur={() => setFocused(false)}
      autoFocus={autoFocus}
      maxLength={maxLength}
      placeholder={placeholder}
      style={{
        width: '100%',
        padding: big ? '10px 0' : '10px 12px',
        background: big ? 'transparent' : c.bgInput,
        border: big ? 'none' : `1px solid ${focused ? c.accent : c.borderLight}`,
        borderBottom: big ? `1px solid ${focused ? c.accent : c.borderLight}` : undefined,
        borderRadius: big ? 0 : 6,
        color: c.textPrimary,
        fontSize: big ? '1.35rem' : '0.92rem',
        fontFamily: big ? t.headingFamily : t.fontFamily,
        fontWeight: big ? 600 : 400,
        outline: 'none',
        textAlign: big ? 'center' : 'left',
        transition: 'border-color 150ms ease',
        boxSizing: 'border-box',
      }}
    />
  );
};

// -----------------------------------------------------------------------
// Textarea — multi-line text input matching TextInput's focus treatment.
// -----------------------------------------------------------------------
export const Textarea = ({ value, onChange, placeholder, theme, rows = 4 }) => {
  const c = theme.colors;
  const t = theme.typography || {};
  const [focused, setFocused] = useState(false);
  return (
    <textarea
      value={value}
      onChange={(e) => onChange(e.target.value)}
      onFocus={() => setFocused(true)}
      onBlur={() => setFocused(false)}
      placeholder={placeholder}
      rows={rows}
      style={{
        width: '100%',
        padding: '10px 12px',
        background: c.bgInput,
        border: `1px solid ${focused ? c.accent : c.borderLight}`,
        borderRadius: 6,
        color: c.textPrimary,
        fontSize: '0.92rem',
        fontFamily: t.fontFamily,
        outline: 'none',
        resize: 'vertical',
        transition: 'border-color 150ms ease',
        boxSizing: 'border-box',
        lineHeight: 1.5,
      }}
    />
  );
};

// -----------------------------------------------------------------------
// Checkbox — custom-styled checkbox with optional label + description.
// -----------------------------------------------------------------------
export const Checkbox = ({ checked, onChange, label, desc, theme, disabled = false }) => {
  const c = theme.colors;
  const t = theme.typography || {};
  return (
    <label style={{
      display: 'flex', alignItems: 'flex-start', gap: 10,
      padding: '8px 10px',
      borderRadius: 6,
      cursor: disabled ? 'not-allowed' : 'pointer',
      opacity: disabled ? 0.58 : 1,
    }}>
      <input
        type="checkbox"
        checked={checked}
        disabled={disabled}
        onChange={(e) => {
          if (!disabled) onChange(e.target.checked);
        }}
        style={{ display: 'none' }}
      />
      <span style={{
        flex: '0 0 16px',
        width: 16, height: 16, marginTop: 1,
        border: `1.5px solid ${checked ? c.accent : c.borderPrimary}`,
        background: checked ? c.accent : 'transparent',
        borderRadius: 3,
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        transition: 'all 120ms ease',
      }}>
        {checked && <span style={{ color: c.textOnAccent, fontSize: '0.7rem', lineHeight: 1, fontWeight: 700 }}>✓</span>}
      </span>
      <div style={{ flex: 1, lineHeight: 1.35 }}>
        <div style={{ fontFamily: t.fontFamily, fontSize: '0.9rem', fontWeight: 600, color: c.textPrimary }}>{label}</div>
        {desc && <div style={{ fontFamily: t.fontFamily, fontSize: '0.76rem', color: c.textMuted, marginTop: 2 }}>{desc}</div>}
      </div>
    </label>
  );
};

// -----------------------------------------------------------------------
// FilterChip — small pill toggle used for the existing-campaign world
// filter row. Pure label, no radio dot (vs WorldModePill).
// -----------------------------------------------------------------------
export const FilterChip = ({ active, onClick, label, theme }) => {
  const c = theme.colors;
  const t = theme.typography || {};
  const [hovered, setHovered] = useState(false);
  return (
    <button
      type="button"
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        padding: '4px 12px',
        background: active ? `${c.accent}1a` : (hovered ? `${c.accent}08` : 'transparent'),
        border: `1px solid ${active ? c.accent : c.borderLight}`,
        borderRadius: 999,
        cursor: 'pointer',
        fontFamily: t.fontFamily,
        fontSize: '0.78rem',
        fontWeight: 500,
        color: active ? c.accentLight : c.textSecondary,
        transition: 'all 120ms ease',
        whiteSpace: 'nowrap',
      }}
    >{label}</button>
  );
};

// -----------------------------------------------------------------------
// WorldModePill — labeled pill with a radio-dot indicator for the
// scratch-origin world association selector (isolated / existing / new).
// -----------------------------------------------------------------------
export const WorldModePill = ({ active, onClick, label, theme }) => {
  const c = theme.colors;
  const t = theme.typography || {};
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 6,
        padding: '5px 12px 5px 10px',
        background: active ? `${c.accent}1a` : 'transparent',
        border: `1px solid ${active ? c.accent : c.borderLight}`,
        borderRadius: 999,
        cursor: 'pointer',
        fontFamily: t.fontFamily,
        fontSize: '0.84rem',
        fontWeight: 600,
        color: active ? c.accentLight : c.textSecondary,
        transition: 'all 120ms ease',
      }}
    >
      <span style={{
        width: 12, height: 12,
        border: `1.5px solid ${active ? c.accent : c.borderPrimary}`,
        borderRadius: '50%',
        display: 'inline-flex',
        alignItems: 'center', justifyContent: 'center',
        flex: '0 0 12px',
      }}>
        {active && (
          <span style={{
            width: 6, height: 6,
            borderRadius: '50%',
            background: c.accent,
          }} />
        )}
      </span>
      {label}
    </button>
  );
};

// -----------------------------------------------------------------------
// ElementModeRow — 3-segment toggle (Off / Copy / Link) for the per-
// element link-mode picker shown after selecting an existing source
// campaign. Each row: label on the left, toggle on the right.
// -----------------------------------------------------------------------
export const ElementModeRow = ({ label, mode, onChange, theme }) => {
  const c = theme.colors;
  const t = theme.typography || {};
  const segments = [
    { value: 'off',  label: 'Off' },
    { value: 'copy', label: 'Copy' },
    { value: 'link', label: 'Link' },
  ];
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 12,
      padding: '6px 10px',
      borderRadius: 6,
    }}>
      <div style={{
        flex: 1,
        fontFamily: t.fontFamily,
        fontSize: '0.88rem',
        color: mode === 'off' ? c.textSecondary : c.textPrimary,
        fontWeight: 500,
      }}>{label}</div>
      <div style={{
        display: 'inline-flex',
        background: 'rgba(0,0,0,0.28)',
        border: `1px solid ${c.borderLight}`,
        borderRadius: 6,
        padding: 2,
        flex: '0 0 auto',
      }}>
        {segments.map((seg) => {
          const active = mode === seg.value;
          return (
            <button
              key={seg.value}
              type="button"
              onClick={() => onChange(seg.value)}
              style={{
                padding: '3px 12px',
                background: active
                  ? (seg.value === 'off' ? c.bgCardHover : c.accent)
                  : 'transparent',
                border: 'none',
                borderRadius: 4,
                color: active
                  ? (seg.value === 'off' ? c.textSecondary : c.textOnAccent)
                  : c.textMuted,
                cursor: 'pointer',
                fontFamily: t.fontFamily,
                fontSize: '0.78rem',
                fontWeight: 600,
                transition: 'all 100ms ease',
              }}
            >{seg.label}</button>
          );
        })}
      </div>
    </div>
  );
};

// -----------------------------------------------------------------------
// ChoiceCard — atmospheric large tile used for the origin selector
// (Scratch / Template / Existing) on Step 1.
// -----------------------------------------------------------------------
export const ChoiceCard = ({ active, onClick, icon, title, desc, theme }) => {
  const c = theme.colors;
  const t = theme.typography || {};
  const [hovered, setHovered] = useState(false);
  return (
    <button
      type="button"
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        background: active ? `${c.accent}14` : (hovered ? `${c.accent}08` : c.bgCardSolid),
        border: `1px solid ${active ? c.accent : c.borderLight}`,
        borderRadius: 10,
        padding: '18px 16px',
        cursor: 'pointer',
        textAlign: 'center',
        transition: 'all 150ms ease',
        boxShadow: active ? `0 0 14px ${c.accentGlow}` : 'none',
        transform: hovered && !active ? 'translateY(-1px)' : 'translateY(0)',
      }}
    >
      <div style={{
        height: 28,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontSize: '1.5rem',
        color: active ? c.accentLight : c.textSecondary,
        marginBottom: 8,
        transition: 'color 180ms ease',
        lineHeight: 1,
      }}>{icon}</div>
      <div style={{
        fontFamily: t.headingFamily,
        fontSize: '1rem',
        fontWeight: 600,
        color: active ? c.accentLight : c.textPrimary,
        marginBottom: 4,
        letterSpacing: '0.02em',
      }}>{title}</div>
      <div style={{
        fontFamily: t.fontFamily,
        fontSize: '0.78rem',
        color: c.textMuted,
        lineHeight: 1.4,
      }}>{desc}</div>
    </button>
  );
};

// -----------------------------------------------------------------------
// TemplateTile — preview card for a template in the template grid.
// -----------------------------------------------------------------------
export const TemplateTile = ({ tpl, active, onClick, theme }) => {
  const c = theme.colors;
  const t = theme.typography || {};
  const [hovered, setHovered] = useState(false);
  return (
    <button
      type="button"
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        background: active ? `${c.accent}14` : (hovered ? `${c.accent}06` : c.bgCardSolid),
        border: `1px solid ${active ? c.accent : c.borderLight}`,
        borderRadius: 8,
        padding: '12px 14px',
        cursor: 'pointer',
        textAlign: 'left',
        transition: 'all 150ms ease',
        boxShadow: active ? `0 0 12px ${c.accentGlow}` : 'none',
        display: 'flex', alignItems: 'flex-start', gap: 10,
      }}
    >
      <div style={{
        fontSize: '1.2rem',
        color: active ? c.accentLight : c.accent,
        lineHeight: 1,
        flex: '0 0 auto',
        marginTop: 1,
      }}>{tpl.icon}</div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{
          fontFamily: t.headingFamily,
          fontSize: '0.95rem',
          fontWeight: 600,
          color: active ? c.accentLight : c.textPrimary,
          marginBottom: 2,
          lineHeight: 1.25,
        }}>{tpl.name}</div>
        <div style={{
          fontFamily: t.fontFamily,
          fontSize: '0.78rem',
          color: c.textSecondary,
          fontStyle: 'italic',
          marginBottom: 3,
          lineHeight: 1.35,
        }}>{tpl.tagline}</div>
        <div style={{
          fontFamily: t.fontFamily,
          fontSize: '0.7rem',
          color: c.textMuted,
          letterSpacing: '0.04em',
        }}>{tpl.genre}</div>
      </div>
    </button>
  );
};

// -----------------------------------------------------------------------
// ExistingCampaignTile — compact pill for an existing campaign in the
// "From existing" picker. Checkmark appears on the active item.
// -----------------------------------------------------------------------
export const ExistingCampaignTile = ({ camp, active, onClick, theme }) => {
  const c = theme.colors;
  const t = theme.typography || {};
  const [hovered, setHovered] = useState(false);
  return (
    <button
      type="button"
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        background: active ? `${c.accent}1a` : (hovered ? `${c.accent}08` : 'transparent'),
        border: `1px solid ${active ? c.accent : c.borderLight}`,
        borderRadius: 999,
        padding: '6px 14px',
        cursor: 'pointer',
        textAlign: 'left',
        transition: 'all 120ms ease',
        boxShadow: active ? `0 0 10px ${c.accentGlow}` : 'none',
        display: 'inline-flex', alignItems: 'center', gap: 8,
      }}
    >
      <div style={{
        fontFamily: t.headingFamily,
        fontSize: '0.88rem',
        fontWeight: 600,
        color: active ? c.accentLight : c.textPrimary,
        whiteSpace: 'nowrap',
      }}>{camp.name}</div>
      {active && (
        <span aria-hidden="true" style={{
          color: c.accent,
          fontSize: '0.85rem',
          marginLeft: 2,
        }}>✓</span>
      )}
    </button>
  );
};

// -----------------------------------------------------------------------
// ThemeButton — small inline ghost button on Step 1's footer. Two-dot
// color swatch + theme + variant label. Clicking opens ThemeVariantModal.
// -----------------------------------------------------------------------
export const ThemeButton = ({ theme, form, onClick }) => {
  const c = theme.colors;
  const t = theme.typography || {};
  const [hovered, setHovered] = useState(false);
  const themeData = THEMES[form.baseTheme] || theme;
  const variants = THEME_VARIANTS[form.baseTheme] || [];
  const variant = variants.find((v) => v.id === form.baseVariant) || variants[0];
  const previewAccent = variant ? variant.accent : themeData.colors.accent;
  const previewLight = variant ? variant.accentLight : themeData.colors.accentLight;
  return (
    <button
      type="button"
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        background: 'transparent',
        border: `1px solid ${hovered ? c.accent : c.borderLight}`,
        borderRadius: 6,
        padding: '7px 12px',
        cursor: 'pointer',
        transition: 'all 120ms ease',
        display: 'inline-flex', alignItems: 'center', gap: 10,
        fontFamily: t.fontFamily,
        fontSize: '0.85rem',
        color: c.textSecondary,
      }}
    >
      <span style={{ display: 'inline-flex', gap: 3, flex: '0 0 auto' }}>
        <span style={{
          width: 10, height: 10, borderRadius: '50%',
          background: previewAccent,
          border: `1px solid ${c.borderLight}`,
        }} />
        <span style={{
          width: 10, height: 10, borderRadius: '50%',
          background: previewLight,
          border: `1px solid ${c.borderLight}`,
        }} />
      </span>
      <span>
        <span style={{ color: c.textMuted }}>Theme: </span>
        <span style={{ color: c.textPrimary, fontWeight: 600 }}>
          {themeData.name}{variant ? ` · ${variant.name}` : ''}
        </span>
      </span>
    </button>
  );
};
